import { useCallback, useMemo } from "react";

import type { QueryClient } from "react-query";

import {
  type AmpProjectLinkKind,
  type AmpProjectLinksBoolExp,
  type AmpProjectsLinkedToQuery,
  useAmpProjectsLinkedToQuery,
  useLinkAmpBoardWorkMutation,
  useUnlinkAmpBoardWorkMutation,
} from "src/graphql";
import { parseProjectIcon } from "src/pages/projects/components/project-icon-set";
import { useBoardErrorToast } from "src/pages/projects/hooks";

/** Refetches every view of linked projects, for a link written outside the app's mutation hooks. */
export function invalidateLinkedProjects(queryClient: QueryClient) {
  return queryClient.invalidateQueries(["AmpProjectsLinkedTo"]);
}

/** One Hightouch object that can be linked to projects, such as a chat or an audience. */
export type ProjectLinkTarget = { kind: AmpProjectLinkKind; id: string };

export type LinkedProject = {
  projectId: string;
  title: string;
  icon: string | null;
  linkId: string;
};

// Past this many chips, the rest fold into one "+N" chip so the row stays one line.
const VISIBLE_PROJECT_CHIPS = 2;

/** The projects to show as chips, and the rest that fold into "+N". */
export function splitProjectChips<T>(projects: T[]): {
  visible: T[];
  hidden: T[];
} {
  return {
    visible: projects.slice(0, VISIBLE_PROJECT_CHIPS),
    hidden: projects.slice(VISIBLE_PROJECT_CHIPS),
  };
}

/**
 * A project's name as plain text, with its emoji in front when it has one. A
 * glyph icon ("icon:rocket") has no text form, so it's left out: pass
 * projectGlyphIcon to the component's icon slot where it should show.
 */
export function projectLabel(project: Pick<LinkedProject, "title" | "icon">) {
  const parsed = parseProjectIcon(project.icon);
  return parsed?.kind === "emoji"
    ? `${parsed.emoji} ${project.title}`
    : project.title;
}

// Matches the link row's column for each kind. An external link has no id column to match on.
const WHERE_BY_KIND: Record<
  Exclude<AmpProjectLinkKind, "external">,
  (id: string) => AmpProjectLinksBoolExp
> = {
  action: (id) => ({ amp_action_id: { _eq: id } }),
  action_plan: (id) => ({ amp_action_plan_id: { _eq: id } }),
  audience: (id) => ({ audience_id: { _eq: id } }),
  // An ad or email links through its Library file.
  ad: (id) => ({ amp_file: { ad_brief_asset_id: { _eq: id } } }),
  brief_version: (id) => ({ ad_studio_project_brief_version_id: { _eq: id } }),
  campaign: (id) => ({ marketing_campaign_id: { _eq: id } }),
  campaign_artifact: (id) => ({ campaign_artifact_id: { _eq: id } }),
  chat_session: (id) => ({ llm_chat_session_id: { _eq: id } }),
  email: (id) => ({ amp_file: { content_email_id: { _eq: id } } }),
  file: (id) => ({ amp_file_id: { _eq: id } }),
  native_delivery_campaign: (id) => ({
    native_delivery_campaign_id: { _eq: id },
  }),
  opportunity: (id) => ({ amp_opportunity_id: { _eq: id } }),
  sync: (id) => ({ sync_id: { _eq: id } }),
};

/** The filter that finds a target's project-level links, or null for a target with no id to match. */
export function linkedProjectsWhere(
  target: ProjectLinkTarget,
): AmpProjectLinksBoolExp | null {
  if (target.kind === "external" || !target.id) return null;
  return WHERE_BY_KIND[target.kind](target.id);
}

/** Each linked project once, in link order. A project the viewer can't read has no row. */
export function toLinkedProjects(
  links: AmpProjectsLinkedToQuery["amp_project_links"],
): LinkedProject[] {
  const seen = new Set<string>();
  return links.flatMap((link) => {
    if (!link.project || seen.has(link.project.id)) return [];
    seen.add(link.project.id);
    return [
      {
        projectId: link.project.id,
        title: link.project.title,
        icon: link.project.icon,
        linkId: link.id,
      },
    ];
  });
}

/**
 * Links a target to a project, or removes a link. Each resolves to whether it
 * worked; a failure shows a toast. The app's mutation default refetches what shows them.
 */
export function useProjectLinkWrites() {
  const showError = useBoardErrorToast();
  const { mutateAsync: linkWork } = useLinkAmpBoardWorkMutation();
  const { mutateAsync: unlinkWork } = useUnlinkAmpBoardWorkMutation();

  const linkToProject = useCallback(
    async (target: ProjectLinkTarget, projectId: string) => {
      try {
        await linkWork({
          projectId,
          taskId: null,
          target: { kind: target.kind, id: target.id },
        });
      } catch (error) {
        showError("Couldn't add to project", error);
        return false;
      }
      return true;
    },
    [linkWork, showError],
  );

  const unlinkFromProject = useCallback(
    async (linkId: string) => {
      try {
        const result = await unlinkWork({ id: linkId });
        if (!result.delete_amp_project_links_by_pk) {
          throw new Error("The link was already removed.");
        }
      } catch (error) {
        showError("Couldn't remove from project", error);
        return false;
      }
      return true;
    },
    [unlinkWork, showError],
  );

  return { linkToProject, unlinkFromProject };
}

/** The open projects one target is linked to, with writes that add and remove its links. */
export function useLinkedProjects(target: ProjectLinkTarget) {
  const where = linkedProjectsWhere(target);
  const { data, isLoading } = useAmpProjectsLinkedToQuery(
    // Never sent without a filter: the query is disabled then.
    { where: where ?? {} },
    { enabled: where !== null },
  );
  const projects = useMemo(
    () => toLinkedProjects(data?.amp_project_links ?? []),
    [data],
  );
  const { linkToProject, unlinkFromProject } = useProjectLinkWrites();

  return {
    projects,
    isLoading: where !== null && isLoading,
    link: (projectId: string) => linkToProject(target, projectId),
    unlink: unlinkFromProject,
  };
}
